import { Children, useEffect, useLayoutEffect, useRef, type ReactNode } from "react";
import { Switch, Route, useLocation, useSearch, Redirect, Router as WouterRouter } from "wouter";
import { queryClient, apiRequest } from "./lib/queryClient";
import { projectsListQuery } from "./lib/projectsQuery";
import { saveProjectsSnapshot } from "./lib/projectsSnapshot";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { AuthProvider } from "@/contexts/AuthContext";
// Lazy: nothing has been said on a first paint (components/ui/app-toaster.tsx).
import { Toaster } from "@/components/ui/app-toaster";
import { TopEdgeSample } from "@/components/ui/top-edge-sample";
import { useCardStatus } from "@/lib/cardStatus";
import { installVisualViewportVars } from "@/lib/visualViewport";
import { installScrollMore } from "@/lib/scrollMore";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Loader } from "@/components/ui/loader";
import { ThemeProvider } from "@/components/ThemeProvider";
import { LanguageProvider } from "@/components/LanguageProvider";
import { WidgetManagerProvider } from "@/contexts/WidgetManagerContext";
import { IntelligenceProvider } from "@/contexts/IntelligenceContext";
import { CompanyThemeProvider } from "@/contexts/CompanyThemeProvider";
import { UIProvider, useUIContext } from "@/contexts/UIContext";
import { AppShell } from "@/components/AppShell";
import { isAdminPath, isCompanyPath, useAppLocation, useAppSearch } from "@/lib/platformLayer";
import { AdminGuard, CompanySettingsGuard } from "@/components/AdminGuard";
/* Platform Settings, Company Settings and every page their rails open are
   preloadable lazies (lib/settingsPreload.ts), so the sidebar can warm them
   and a warm one renders without suspending. */
import {
  AdminRoadmapPage, AppSettingsPage, CompaniesPage, CompanyAdminPage, CompanyModalPage,
  CompetitorAnalysisPage, DocumentLibraryPage, IntegrationsPage, PlatformModalPage, PlatformReportsPage,
  ProjectListPage, QuotesPage, RolesPage, UsagePagePage, UserLogPagePage, UsersPage,
} from "@/lib/settingsPreload";
import {
  AgentPortalPage, AvailabilityPage, CampaignsPage, ContactsPage, DashboardPage, EmailMarketingPage, WebsiteAnalyticsPage, OffersPage, ShowroomPage,
  ProjectPickerPage, ProjectSettingsPage, ReportsPage, SalesListPage, DepositsPage, SigningPage,
  TasksPage, warmProjectPages,
} from "@/lib/projectPagePreload";
import { canWarmIdle } from "@/lib/lazyPreload";
import { ServiceNotificationBanner } from "@/components/ServiceNotificationBanner";
import { useAuth } from "@/hooks/useAuth";
import { focusPending } from "@/lib/focusPending";
import { InstantPages, PageTransition } from "@/components/PageTransition";
import { lazy, Suspense } from "react";
import { ChunkErrorBoundary } from "@/components/ChunkErrorBoundary";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { StepUpDialog } from "@/components/StepUpDialog";
import { consumeReturnTo } from "@/lib/sessionEnd";
import { landAfterSignIn } from "@/lib/landAfterSignIn";
import { usePageViews, useAnalyticsIdentity } from "@/lib/analytics";
import { useTranslation } from "@/hooks/useTranslation";
import { canAccessPlatformSettings } from "@/lib/platformAccess";
/* The bottom cards — update, install, notifications, passkey — through one
   queue (PromptLayer), lazy: none of them can appear on first paint, so none
   of them belongs in the entry chunk. `onUpdateReady` replays a pending update
   to a late subscriber, so a lazy UpdatePrompt misses nothing. */
const PromptLayer = lazy(() => import("@/components/PromptLayer"));
const UpdatePrompt = lazy(() => import("@/components/UpdatePrompt").then((m) => ({ default: m.UpdatePrompt })));
/* The screen a company locked for want of a credit card sees instead of the
   app (shared/cardRule.ts). Lazy: it carries the card form and Stripe.js, and
   almost nobody ever sees it. */
const CardGate = lazy(() => import("@/components/CardGate"));
import { RouteProgressProvider, useRouteProgress } from "@/components/RouteProgress";
import { LazyMotion, MotionConfig } from "framer-motion";
import { PageLoader } from "@/components/ui/page-loader";
import { UserPreferencesSync } from "@/components/UserPreferencesSync";
import { IdleTimeout } from "@/components/IdleTimeout";

// The sign-in screen is the only page that is genuinely first paint for the
// common case, so it is the only one still eager.
import Login from "@/pages/Login";
import { isPublicPagePath } from "@/lib/publicPages";

/* Every other public page is either a click away from somewhere else (forgot
 * password, reset, the 2FA step, the two policy pages) or the landing page for
 * a visitor who is not our user at all — a buyer opening a quote or a
 * registration link. Static imports put all seven in the entry chunk, so every
 * signed-in person downloaded a buyer's registration form on every first load,
 * and every buyer downloaded the sign-in screen and the other buyer's page.
 *
 * warmPublicChunk() below is what stops this costing the buyer a round trip.
 */
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const TwoFactorVerificationPage = lazy(() => import("@/pages/TwoFactorVerification"));
const PublicRegistration = lazy(() => import("@/pages/PublicRegistration"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("@/pages/TermsOfUse"));
const PublicQuoteView = lazy(() => import("@/pages/PublicQuoteView"));
// A broker answering a reassigned worksheet from the email (no sign-in; the link is the credential).
const PublicWorksheetResponse = lazy(() => import("@/pages/PublicWorksheetResponse"));
// A buyer signing their agreement on their own device (no sign-in; the emailed code is the credential).
const SignContract = lazy(() => import("@/pages/SignContract"));
const IdCapture = lazy(() => import("@/pages/IdCapture"));
const ConfirmEmail = lazy(() => import("@/pages/ConfirmEmail"));
const Unsubscribe = lazy(() => import("@/pages/Unsubscribe"));
const PurchaseHome = lazy(() => import("@/pages/PurchaseHome"));
const ReviewEnvelope = lazy(() => import("@/pages/ReviewEnvelope"));

/**
 * Ask for the chunk this URL is going to need NOW, at module evaluation,
 * instead of when React first renders the route.
 *
 * The distinction matters here and nowhere else in this file. None of the
 * unauthenticated branches render until `GET /api/auth/user` has settled, so a
 * lazy public page would otherwise wait out a full network round trip before
 * the browser was even told the chunk existed — two requests in series on the
 * one screen where the visitor is a customer's customer and has no patience for
 * it. Warming here overlaps the two.
 *
 * Deliberately not `requestIdleCallback`, unlike prefetchAuthenticatedRoutes()
 * below: that one is warming a page nobody has asked for yet, and this one is
 * fetching the page already on screen.
 */
function warmPublicChunk() {
  if (typeof window === "undefined") return;
  const path = window.location.pathname;
  if (path.startsWith("/register/")) void import("@/pages/PublicRegistration");
  else if (path.startsWith("/quote/")) void import("@/pages/PublicQuoteView");
  else if (path.startsWith("/worksheet-response/")) void import("@/pages/PublicWorksheetResponse");
  else if (path.startsWith("/sign/")) void import("@/pages/SignContract");
  else if (path.startsWith("/id/")) void import("@/pages/IdCapture");
  else if (path.startsWith("/confirm-email/")) void import("@/pages/ConfirmEmail");
  else if (path.startsWith("/unsubscribe/")) void import("@/pages/Unsubscribe");
  else if (path.startsWith("/review/")) void import("@/pages/ReviewEnvelope");
  else if (path === "/documents") void import("@/pages/PurchaseHome");
  else if (path.startsWith("/roadmap/")) void import("@/pages/Roadmap");
  else if (path.startsWith("/reset-password")) void import("@/pages/ResetPassword");
}
warmPublicChunk();

/**
 * One boundary around a whole Switch of lazy public pages.
 *
 * The authenticated tree has had this since the routes were split; the three
 * unauthenticated Switches below had only a bare Suspense around Roadmap,
 * because everything else in them used to be static. A chunk that 404s after a
 * deploy has to reload the page to recover, and without a boundary here it
 * would instead take down the login screen — the one screen from which a user
 * cannot navigate anywhere to escape it.
 */
function PublicPages({ children }: { children: ReactNode }) {
  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<div className="min-h-screen" />}>{children}</Suspense>
    </ChunkErrorBoundary>
  );
}

// Post-login pages are code-split.
//
// These were previously static imports, "to eliminate double loading" — but
// every one of them is unreachable until the user has signed in, so the only
// thing that bought was making the login screen download the entire
// application first. That put the entry chunk at ~2.8 MB (711 kB gzip).
//
// They stay lazy and are warmed by prefetchAuthenticatedRoutes() below once the
// app is idle and the user is authenticated, so navigation still feels instant
// without charging first paint for it. Bindings keep their original names, so
// the route table below is unchanged.
/* Public, unauthenticated, and carries the whole 263-row roadmap dataset —
   lazy so it never lands in anyone else's bundle. */
const Roadmap = lazy(() => import("@/pages/Roadmap"));
const Companies = CompaniesPage;
const Roles = RolesPage;
const Users = UsersPage;
const AppSettings = AppSettingsPage;
const Quotes = QuotesPage;
const QuoteBuilder = lazy(() => import("@/pages/QuoteBuilder"));
const QuoteDetail = lazy(() => import("@/pages/QuoteDetail"));
const ItemLibrary = lazy(() => import("@/pages/ItemLibrary"));
const QuoteTemplates = lazy(() => import("@/pages/QuoteTemplates"));
const TemplateBuilder = lazy(() => import("@/pages/TemplateBuilder"));
const AdminHome = lazy(() => import("@/pages/AdminHome"));
const ProjectList = ProjectListPage;
const ProjectPicker = ProjectPickerPage;
const Dashboard = DashboardPage;
const Contacts = ContactsPage;

const PasskeysSettings = lazy(() => import("@/pages/PasskeysSettings"));

// Warm the chunks a signed-in user is most likely to open next, once the
// browser is idle. This is what preserves the instant navigation the eager
// imports were reaching for, without charging first paint for it.
/* Stable empty-array identity for the useQuery default below. Module scope is
   the whole point: it is created once, so the reference never changes and
   cannot invalidate an effect's dependency array. Treat as read-only. */
const EMPTY_PROJECTS: any[] = [];

let hasPrefetchedRoutes = false;
function prefetchAuthenticatedRoutes(user: { role?: string | null }) {
  if (hasPrefetchedRoutes) return;
  hasPrefetchedRoutes = true;
  // Not on a 4 GB device or under Save-Data: each opens when it is asked for.
  if (!canWarmIdle()) return;

  const warm = () => {
    // Through the routes' own lazies, not bare import(): a bare import warms
    // the chunk but the route's lazy still suspends on first render and
    // shows its fallback for React's 300ms reveal throttle (lib/lazyPreload.ts).
    void DashboardPage.preload().catch(() => {});
    void ProjectPickerPage.preload().catch(() => {});
    void ContactsPage.preload().catch(() => {});
    // Billing is SaleFish staff only (AdminGuard), and it carries recharts —
    // ~92 KB gzip that nobody else can open. Warm it only for who can.
    if (canAccessPlatformSettings(user)) void QuotesPage.preload().catch(() => {});
    // The two dialogs a reader opens from anywhere (K9): a contact from a
    // notification or search, and the account panel from the menu's foot.
    // Warmed on idle so the first open is not a chunk fetch behind a click.
    void import("@/components/ContactDetailModal");
    void import("@/components/AccountSettingsDialog");
  };

  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(warm, { timeout: 3000 });
  } else {
    window.setTimeout(warm, 1500);
  }
}

// Lazy load less frequently accessed pages
const LazyTasks = TasksPage;
const LazyCampaigns = CampaignsPage;
const LazyOffers = OffersPage;
const LazySalesList = SalesListPage;
const LazySigning = SigningPage;
const LazyAvailability = AvailabilityPage;
const LazyInventorySetup = lazy(() => import("@/pages/InventorySetup"));
const LazySalefishIntegrations = lazy(() => import("@/pages/SalefishIntegrations"));
const LazyReports = ReportsPage;
const LazyDeposits = DepositsPage;
const LazyCurrentProjectSettings = ProjectSettingsPage;
const LazyEmailMarketing = EmailMarketingPage;
const LazyWebsiteAnalytics = WebsiteAnalyticsPage;
const LazyShowroom = ShowroomPage;
const LazyAgentPortal = AgentPortalPage;
const LazyCompanyAdmin = CompanyAdminPage;
const LazyIntegrations = IntegrationsPage;
const LazyAdminTranslations = lazy(() => import("@/pages/AdminTranslations"));
const LazyUsagePage = UsagePagePage;
const LazyCompetitorAnalysis = CompetitorAnalysisPage;
const LazyAdminRoadmap = AdminRoadmapPage;
const LazyPlatformReports = PlatformReportsPage;
const LazyUserLogPage = UserLogPagePage;
const LazyDocumentLibrary = DocumentLibraryPage;

/** A redirect that carries the query string with it — a moved page keeps its filters. */
function RedirectKeepingQuery({ to }: { to: string }) {
  const search = useSearch();
  return <Redirect to={search ? `${to}?${search}` : to} replace />;
}


/**
 * Every SaleFish Admin route. Rendered in two places: the app's own Switch
 * (a phone or portrait tablet, where SaleFish Admin is a page) and
 * `PlatformModal` (from the sidenav breakpoint up, where it is a layer over the
 * page — see lib/platformLayer.ts). A fragment, which wouter's Switch flattens.
 */
function adminRoutes() {
  /* A flat, keyed ARRAY of <Route>s, not a fragment: wouter's Switch hands the
     matched child a `match` prop, and handed to a Fragment that prop is
     invalid — React warned on every Platform Settings render. */
  return Children.toArray((
    <>
          {/* Admin Routes - All guarded */}
          <Route path="/admin">
            <PageTransition><AdminGuard><AdminHome /></AdminGuard></PageTransition>
          </Route>
          <Route path="/admin/settings">
            <PageTransition><AdminGuard><AppSettings /></AdminGuard></PageTransition>
          </Route>
          <Route path="/admin/projects">
            <PageTransition><AdminGuard><ProjectList /></AdminGuard></PageTransition>
          </Route>
          <Route path="/admin/companies">
            <PageTransition><AdminGuard><Companies /></AdminGuard></PageTransition>
          </Route>
          <Route path="/admin/billing">
            <PageTransition><AdminGuard><Quotes /></AdminGuard></PageTransition>
          </Route>
          <Route path="/admin/billing/dashboard">
            <PageTransition><AdminGuard><Quotes /></AdminGuard></PageTransition>
          </Route>
          <Route path="/admin/billing/quotes">
            <PageTransition><AdminGuard><Quotes /></AdminGuard></PageTransition>
          </Route>
          <Route path="/admin/billing/contacts">
            <PageTransition><AdminGuard><Quotes /></AdminGuard></PageTransition>
          </Route>
          <Route path="/admin/billing/templates-items">
            <PageTransition><AdminGuard><Quotes /></AdminGuard></PageTransition>
          </Route>
          <Route path="/admin/billing/new">
            <PageTransition><AdminGuard><QuoteBuilder /></AdminGuard></PageTransition>
          </Route>
          {/* A record route keys its page on the record: one quote's state must
              not carry into the next (the Router no longer remounts per path). */}
          <Route path="/admin/billing/quotes/:id">
            {(params) => <PageTransition key={params.id}><AdminGuard><QuoteDetail /></AdminGuard></PageTransition>}
          </Route>
          <Route path="/admin/billing/edit/:id">
            {(params) => <PageTransition key={params.id}><AdminGuard><QuoteBuilder /></AdminGuard></PageTransition>}
          </Route>
          <Route path="/admin/billing/item-library">
            <PageTransition><AdminGuard><ItemLibrary /></AdminGuard></PageTransition>
          </Route>
          <Route path="/admin/billing/templates">
            <PageTransition><AdminGuard><QuoteTemplates /></AdminGuard></PageTransition>
          </Route>
          <Route path="/admin/billing/template/:id">
            {(params) => <PageTransition key={params.id}><AdminGuard><TemplateBuilder /></AdminGuard></PageTransition>}
          </Route>
          <Route path="/admin/roles">
            <PageTransition><AdminGuard><Roles /></AdminGuard></PageTransition>
          </Route>
          <Route path="/admin/users">
            <PageTransition><AdminGuard><Users /></AdminGuard></PageTransition>
          </Route>
          <Route path="/admin/integrations">
            <PageTransition><AdminGuard><LazyIntegrations /></AdminGuard></PageTransition>
          </Route>
          <Route path="/admin/translations">
            <PageTransition><AdminGuard><LazyAdminTranslations /></AdminGuard></PageTransition>
          </Route>
          <Route path="/admin/usage/dashboard">
            <PageTransition><AdminGuard><LazyUsagePage /></AdminGuard></PageTransition>
          </Route>
          {/* Usage → Reports folded into Platform Settings → Reports (the same
              queries, server/lib/usageReports.ts). The old path still lands. */}
          <Route path="/admin/usage/reports">
            <Redirect to="/admin/reports" replace />
          </Route>
          {/* Cross-company reports — PropPay's platform Reports (SUPER_ADMIN;
              the server refuses anyone else). */}
          <Route path="/admin/reports">
            <PageTransition><AdminGuard><LazyPlatformReports /></AdminGuard></PageTransition>
          </Route>
          {/* The User log moved out of Usage into its own section (Andrew,
              2026-09-28). Old links — and their ?kind=&q= filters — still land. */}
          <Route path="/admin/usage/log">
            <RedirectKeepingQuery to="/admin/user-log" />
          </Route>
          <Route path="/admin/user-log">
            <PageTransition><AdminGuard><LazyUserLogPage /></AdminGuard></PageTransition>
          </Route>
          {/* SaleFish's own documents — terms, privacy, the Registration API
              guide — read and downloaded in the app (PropPay's library). */}
          <Route path="/admin/documents">
            <PageTransition><AdminGuard><LazyDocumentLibrary /></AdminGuard></PageTransition>
          </Route>
          <Route path="/admin/usage">
            <Redirect to="/admin/usage/dashboard" replace />
          </Route>
          {/* The console rebuild & roadmap — the public /roadmap/<token> page,
              embedded for staff (no token needed behind AdminGuard). */}
          <Route path="/admin/roadmap">
            <PageTransition><AdminGuard><LazyAdminRoadmap embedded /></AdminGuard></PageTransition>
          </Route>
          <Route path="/admin/competitor-analysis/:group/:sub">
            <PageTransition><AdminGuard><LazyCompetitorAnalysis /></AdminGuard></PageTransition>
          </Route>
          <Route path="/admin/competitor-analysis/:group">
            <PageTransition><AdminGuard><LazyCompetitorAnalysis /></AdminGuard></PageTransition>
          </Route>
          <Route path="/admin/competitor-analysis">
            <Redirect to="/admin/competitor-analysis/overview" replace />
          </Route>
    </>
  ).props.children);
}

/**
 * Every Company Settings route, rendered in the same two places as
 * `adminRoutes()`: the app's own Switch on a phone, `CompanyModal` from the
 * sidenav breakpoint up. Company Admins and SaleFish Admins only.
 */
function companyRoutes() {
  return Children.toArray((
    <>
          {/* The separate Stripe billing page was folded into Features &
              Billing on 2026-09-30; its URL (billing alerts link to it) lands there. */}
          <Route path="/company-admin/billing">
            <Redirect to="/company-admin/features" replace />
          </Route>
          <Route path="/company-admin">
            <PageTransition><CompanySettingsGuard><LazyCompanyAdmin /></CompanySettingsGuard></PageTransition>
          </Route>
          <Route path="/company-admin/:tab*">
            <PageTransition><CompanySettingsGuard><LazyCompanyAdmin /></CompanySettingsGuard></PageTransition>
          </Route>
    </>
  ).props.children);
}

/* Both settings modals are lazy, and fetched only on their own paths: every
   visitor would otherwise carry them in the first-load budget for screens few
   of them open. */
const LazyPlatformModal = PlatformModalPage;
const LazyCompanyModal = CompanyModalPage;

/** The company routes as their modal renders them, against the REAL location. */
function CompanyLayer() {
  const [location] = useLocation();
  if (!isCompanyPath(location)) return null;
  return (
    <ChunkErrorBoundary fallback={null}>
      <Suspense fallback={null}>
        <LazyCompanyModal>
          <InstantPages.Provider value>
          <ChunkErrorBoundary resetKey={location}>
            <Suspense fallback={<PageLoader />}>
              <Switch location={location}>{companyRoutes()}</Switch>
            </Suspense>
          </ChunkErrorBoundary>
          </InstantPages.Provider>
        </LazyCompanyModal>
      </Suspense>
    </ChunkErrorBoundary>
  );
}

/** The admin routes as the modal renders them, against the REAL location. */
function PlatformLayer() {
  const [location] = useLocation();
  if (!isAdminPath(location)) return null;
  // The boundary resets on the location, as the app's own does, so a chunk
  // error in one admin screen does not stick to the next; each route's own key
  // (Children.toArray) is what mounts a different screen fresh.
  return (
    <ChunkErrorBoundary fallback={null}>
      <Suspense fallback={null}>
        <LazyPlatformModal>
          <InstantPages.Provider value>
          <ChunkErrorBoundary resetKey={location}>
            <Suspense fallback={<PageLoader />}>
              <Switch location={location}>{adminRoutes()}</Switch>
            </Suspense>
          </ChunkErrorBoundary>
          </InstantPages.Provider>
        </LazyPlatformModal>
      </Suspense>
    </ChunkErrorBoundary>
  );
}

/* ── Scroll position per history entry (Back restores it) ───────────────────
   Each entry is stamped with a key in its own `history.state` — merged, so a
   route's own state (`{ fromList: true }`) is kept — once, on arrival. The
   positions themselves live in memory: a reload starts at the top, which is
   what a reload should do. */
const SCROLL_KEY_FIELD = '__sfScrollKey';
const scrollPositions = new Map<string, number>();
let entrySeq = 0;

function historyEntryKey(): string | null {
  try {
    const state = window.history.state as unknown;
    if (state != null && (typeof state !== 'object' || Array.isArray(state))) return null;
    const existing = (state as Record<string, unknown> | null)?.[SCROLL_KEY_FIELD];
    if (typeof existing === 'string') return existing;
    const key = `${Date.now().toString(36)}.${(entrySeq++).toString(36)}`;
    window.history.replaceState({ ...((state as object | null) ?? {}), [SCROLL_KEY_FIELD]: key }, '');
    return key;
  } catch {
    return null;
  }
}

/* Which navigation was a Back/Forward. Listened for HERE, at module load, not
   in an effect: React renders a popstate synchronously inside wouter's own
   listener, so a listener added later (from a component) fires only after the
   new page's layout effect has already treated the pop as a forward
   navigation and scrolled to the top. Measured in WebKit, 2026-10-02. */
let lastPop: { path: string; at: number } | null = null;
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => {
    lastPop = { path: window.location.pathname, at: Date.now() };
  });
}

function takePop(): boolean {
  const pop = lastPop;
  lastPop = null;
  return !!pop && pop.path === window.location.pathname && Date.now() - pop.at < 1000;
}

function rememberScroll(key: string, y: number) {
  scrollPositions.set(key, y);
  // A long session visits a lot of entries; the oldest are never coming back.
  if (scrollPositions.size > 200) scrollPositions.delete(scrollPositions.keys().next().value!);
}

function savedScroll(key: string): number | undefined {
  return scrollPositions.get(key);
}

/** Put the window back at `y`, frame by frame until the page is tall enough
    for it (a lazy chunk or a cached query can land a few frames after the
    route) and has HELD there for a few frames — a page's own mount effect
    can still scroll after this first lands — for at most ~1.5s, and never
    against the reader's own hand. */
function restoreScroll(y: number): () => void {
  let frame = 0;
  let tries = 0;
  let held = 0;
  let stopped = false;
  const INPUT = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    cancelAnimationFrame(frame);
    for (const t of INPUT) window.removeEventListener(t, stop);
  };
  for (const t of INPUT) window.addEventListener(t, stop, { passive: true });
  const tick = () => {
    if (stopped) return;
    if (Math.abs(window.scrollY - y) > 1) {
      window.scrollTo({ top: y, left: 0, behavior: 'instant' });
      held = 0;
    }
    held = Math.abs(window.scrollY - y) <= 1 ? held + 1 : 0;
    if (held >= 6 || ++tries > 90) return stop();
    frame = requestAnimationFrame(tick);
  };
  tick();
  return stop;
}

function Router() {
  const [location] = useLocation();
  const { projectId, projectName, setProjectContext, setProjectId, setGlobalContext } = useUIContext();
  const { user } = useAuth();
  const { start: startProgress } = useRouteProgress();
  const prevPathnameRef = useRef(location.split('?')[0]);

  // Once signed in, warm the common post-login route chunks in idle time.
  useEffect(() => {
    if (user) prefetchAuthenticatedRoutes(user);
  }, [user]);

  // Inside a project, warm the rest of its pages once idle, so the first
  // visit to each is not a "Loading…" flash (lib/projectPagePreload.ts).
  const inProject = !!user && /^\/projects\/[^/]+\//.test(location);
  useEffect(() => (inProject ? warmProjectPages() : undefined), [inProject]);

  // Track page visits on route change
  useEffect(() => {
    const slug = location.replace(/^\//, "").split("/")[0] || "home";
    // `background`: a page-view log is never a reason to send someone to the
    // sign-in page or raise a step-up (K6).
    // The path (never the query string) so Platform Settings → Usage → User
    // log can name the screen rather than its first segment. Token-bearing
    // public paths are never logged — they carry a buyer's secret.
    const path = location.split(/[?#]/)[0];
    const tokenBearing = /^\/(quote|register|roadmap)\//.test(path);
    apiRequest("POST", "/api/usage/events", {
      module: slug,
      action: "page_visit",
      ...(tokenBearing ? {} : { metadata: { path } }),
    }, { background: true }).catch(() => {});
  }, [location]);

  // Who is using the app, for analytics: a hashed id and a role, never the
  // email itself (lib/analytics.ts). Off unless VITE_GA_MEASUREMENT_ID is set.
  useAnalyticsIdentity(user);

  // Trigger top progress bar on pathname change (not query param changes like
  // tab switches). A LAYOUT effect: the page's PageTransition completes the bar
  // in a passive effect, and passive effects run after every layout effect of
  // the commit — so start always precedes complete (components/RouteProgress).
  useLayoutEffect(() => {
    const pathname = location.split('?')[0];
    if (pathname !== prevPathnameRef.current) {
      prevPathnameRef.current = pathname;
      startProgress();
    }
  }, [location, startProgress]);

  // Get current user to determine project fetch strategy
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  /* `= EMPTY_PROJECTS`, not `= []`.
  
     A `[]` default in a destructuring pattern is evaluated on EVERY render
     while the query has no data, so it is a new array reference each time. This
     value is a dependency of the route-context effect below, which made that
     effect re-run on every render — and on an /admin route its body calls
     setGlobalContext(), which re-rendered the provider, which rendered this
     component again. That was one of the two feedback paths behind the
     "Maximum update depth exceeded" loop (see contexts/UIContext.tsx for the
     other). A module-level constant is referentially stable, so the dependency
     only changes when real data arrives. */
  const { data: projectsData = EMPTY_PROJECTS } = useQuery<any[]>({
    ...projectsListQuery(isSuperAdmin),
    enabled: !!user, // Only fetch when user is loaded
  });
  // The device's copy of this list: what the next launch draws, and decodes the
  // pictures of, before the projects answer (lib/projectsSnapshot.ts).
  useEffect(() => {
    if (projectsData !== EMPTY_PROJECTS && user?.id) saveProjectsSnapshot(user.id, projectsData);
  }, [projectsData, user?.id]);

  /* A new page starts at the top and puts focus on it (G3, PropPay's rule) —
     on a PATH change only. A `?tab=` or `?openContact=` change is the same
     page in a new state, and jumping to the top under the reader, or pulling
     focus off the tab they just pressed, is wrong for both. Focus goes to
     <main> (tabIndex -1, set in AppShell) without scrolling, so a
     screen-reader user hears the new page rather than staying on the link
     they left, and keyboard Tab resumes from the page's top. Skipped on first
     load (the browser is already there) and while a dialog is open (it owns
     focus; the page underneath will not be read until it closes).

     BACK RETURNS TO WHERE THE READER WAS. Every history entry carries a key
     (historyEntryKey), the window's scroll is recorded against the entry on
     screen, and a popstate onto an entry with a recorded position restores it
     instead of going to the top — the way a native list keeps its place when
     you come back from a record.

     ONE instant scroll, in a layout effect, so it lands before the new page
     paints. It used to be three passes here (now, the next frame, and 100ms
     later) and a fourth in PageTransition, each one animated by
     `html { scroll-behavior: smooth }` — the top of every page glided into
     place, and the late passes overrode useFocusParam's scroll to one row. */
  const pathname = location.split('?')[0];
  const projectKey = /^\/projects\/([^/]+)/.exec(pathname)?.[1] ?? '';
  const firstPathRef = useRef(true);
  const entryKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    const onScroll = () => {
      const key = entryKeyRef.current;
      if (key) rememberScroll(key, window.scrollY);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  useLayoutEffect(() => {
    const key = historyEntryKey();
    entryKeyRef.current = key;
    const isBack = takePop();
    const saved = key ? savedScroll(key) : undefined;

    let stopRestore: (() => void) | undefined;
    let focusRaf = 0;
    if (firstPathRef.current) {
      firstPathRef.current = false;
    } else {
      if (isBack && saved !== undefined) stopRestore = restoreScroll(saved);
      // Unless the page is about to scroll to one record itself (`?focus=`).
      else if (!focusPending()) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

      focusRaf = requestAnimationFrame(() => {
        if (document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]')) return;
        const main = document.getElementById('main-content');
        if (main && !main.contains(document.activeElement)) main.focus({ preventScroll: true });
      });
    }

    return () => {
      stopRestore?.();
      cancelAnimationFrame(focusRaf);
    };
  }, [pathname]);

  // Effect to set context based on route.
  // NOTE: /company-admin is intentionally NOT listed here — the sidebar should
  // stay in whatever context the user was in before navigating to Company Settings.
  useEffect(() => {
    if (location.startsWith('/admin')) {
      setGlobalContext();
    } else if (location.startsWith('/projects/') && location.split('/').length >= 3) {
      const routeProjectId = location.split('/')[2];
      if (!routeProjectId || routeProjectId === 'undefined') return;

      const project = projectsData.find((p: any) => p.id === routeProjectId);

      if (project) {
        // Full data available — update if id or name changed
        if (project.id !== projectId || project.projectName !== projectName) {
          setProjectContext(project.id, project.projectName);
        }
      } else if (routeProjectId !== projectId) {
        // Data not yet loaded — set projectId from URL immediately so queries can fire.
        // Name will be filled in once projectsData resolves and this effect re-runs.
        setProjectId(routeProjectId);
      }
    }
  }, [location, projectId, projectName, projectsData, setGlobalContext, setProjectContext, setProjectId]);

  /*
      No AnimatePresence here, deliberately.

      It used to wrap this as <AnimatePresence mode="wait">, and that could
      deadlock the router. AnimatePresence tracks its DIRECT child for presence,
      and the direct child was ChunkErrorBoundary — a plain class component that
      never registers. The real motion.div lives several levels down, inside
      PageTransition, behind Suspense and Switch. With mode="wait" the incoming
      page is withheld until the outgoing one reports exit-complete, so whenever
      that signal did not arrive — the subtree threw, or the next route's lazy
      chunk suspended — the new route never mounted at all.

      What that looked like: the URL and breadcrumb updated, the old page stayed
      frozen at ~0 opacity, and the new page never rendered. Because it never
      mounted, none of its queries fired either, so the server saw ZERO requests
      and the whole thing looked like a hang. Measured on /admin/companies:
      the only animated wrapper still held the /home content, inline
      opacity: 1 but computed opacity 0.0004. A refresh always "fixed" it,
      because a full page load has no transition to deadlock. This is also why
      landing on /home after 2FA appeared to stall.

      PageTransition still animates entry via initial/animate on mount, which is
      its stated intent ("instant exit, smooth entrance only"). Its `exit`
      variant is inert without AnimatePresence — that is the behaviour we want,
      and it cannot wedge the router.
  */
  return (
      <ChunkErrorBoundary resetKey={pathname}>
      {/* A page's chunk in flight shows the page's SHAPE (K12), not a blank
          screen-height div that then jumps to content. */}
      <Suspense fallback={<PageLoader />}>
        {/* NOT keyed on the location: that remounted the whole page on every
            path change — Availability's Matrix → Table → Site plan lost its
            filters, refetched, re-subscribed and replayed its entrance. Each
            <Route> carries a key instead, so a different route mounts fresh
            and the same route (a view segment, a param) keeps its page. The
            Switch is keyed on the PROJECT alone: another project's page must
            not inherit this one's state. */}
        <Switch location={location} key={projectKey}>
          {/* The old Home page is retired (Andrew, 2026-09-28): everyone signs
              in to the project picker. /home stays as a redirect so bookmarks,
              installed-app start URLs and old push links still land. */}
          <Route key="/home" path="/home">
            <Redirect to="/projects" replace />
          </Route>

          {adminRoutes()}

          {companyRoutes()}

          {/* Project List */}
          <Route key="/projects" path="/projects">
            <PageTransition><ProjectPicker /></PageTransition>
          </Route>

          {/* Project Routes - Pattern: /projects/:projectId/* */}
          {/* Redirect undefined project IDs to Projects page */}
          <Route key="/projects/undefined" path="/projects/undefined">
            <Redirect to="/projects" replace />
          </Route>
          <Route key="/projects/undefined/:rest*" path="/projects/undefined/:rest*">
            <Redirect to="/projects" replace />
          </Route>
          
          <Route key="/projects/:projectId" path="/projects/:projectId">
            {(params) => <Redirect to={`/projects/${params.projectId}/dashboard`} replace />}
          </Route>
          <Route key="/projects/:projectId/dashboard" path="/projects/:projectId/dashboard">
            {(params) => (
              <PageTransition>
                <Dashboard selectedProject={params.projectId} selectedPhase="" />
              </PageTransition>
            )}
          </Route>
          <Route key="/projects/:projectId/contacts" path="/projects/:projectId/contacts">
            <PageTransition><Contacts /></PageTransition>
          </Route>
          <Route key="/projects/:projectId/tasks" path="/projects/:projectId/tasks">
            <PageTransition><LazyTasks /></PageTransition>
          </Route>
          <Route key="/projects/:projectId/campaigns" path="/projects/:projectId/campaigns">
            {(params) => <Redirect to={`/projects/${params.projectId}/email-marketing`} replace />}
          </Route>
          <Route key="/projects/:projectId/offers" path="/projects/:projectId/offers">
            <PageTransition><LazyOffers /></PageTransition>
          </Route>
          <Route key="/projects/:projectId/sales-list" path="/projects/:projectId/sales-list">
            <PageTransition><LazySalesList /></PageTransition>
          </Route>
          {/* Sales → Signing and Sales → Deposits (Sales & Contracts plan, Decision 12).
              They were the Signing Queue and Sales Management placeholders; old links land here. */}
          <Route key="/projects/:projectId/signing" path="/projects/:projectId/signing">
            <PageTransition><LazySigning /></PageTransition>
          </Route>
          <Route key="/projects/:projectId/deposits" path="/projects/:projectId/deposits">
            <PageTransition><LazyDeposits /></PageTransition>
          </Route>
          <Route key="/projects/:projectId/signing-queue" path="/projects/:projectId/signing-queue">
            {(params) => <RedirectKeepingQuery to={`/projects/${params.projectId}/signing`} />}
          </Route>
          <Route key="/projects/:projectId/sales-management" path="/projects/:projectId/sales-management">
            {(params) => <RedirectKeepingQuery to={`/projects/${params.projectId}/deposits`} />}
          </Route>
          <Route key="availability" path="/projects/:projectId/availability">
            <PageTransition><LazyAvailability /></PageTransition>
          </Route>
          <Route key="availability" path="/projects/:projectId/availability/*">
            <PageTransition><LazyAvailability /></PageTransition>
          </Route>

          <Route key="/projects/:projectId/inventory/setup" path="/projects/:projectId/inventory/setup">
            <PageTransition><LazyInventorySetup /></PageTransition>
          </Route>
          {/* The sync's status, errors and history: SaleFish Admins only (shared/legacySyncErrors.ts). */}
          <Route key="/projects/:projectId/integrations/salefish" path="/projects/:projectId/integrations/salefish">
            <PageTransition><AdminGuard><LazySalefishIntegrations /></AdminGuard></PageTransition>
          </Route>
          {/* The off-menu Worksheet Queue and Submit Worksheet pages were retired
              into Offers (2026-10-24, docs/plans/2026-10-24-offers.md): the
              queue's list and filters are the Offers page's, and a worksheet
              started from a unit opens New worksheet there with that unit as
              its first choice (`?new=<unitId>`). Old links land there. */}
          <Route key="/projects/:projectId/inventory/worksheets" path="/projects/:projectId/inventory/worksheets">
            {(params) => <RedirectKeepingQuery to={`/projects/${params.projectId}/offers`} />}
          </Route>
          <Route key="/projects/:projectId/inventory/submit-worksheet/:unitId" path="/projects/:projectId/inventory/submit-worksheet/:unitId">
            {(params) => <Redirect to={`/projects/${params.projectId}/offers?new=${encodeURIComponent(params.unitId)}`} replace />}
          </Route>
          {/* The standalone matrix was retired 2026-10-24: Availability's Matrix view is the one. */}
          <Route key="/projects/:projectId/matrix" path="/projects/:projectId/matrix">
            {(params) => <Redirect to={`/projects/${params.projectId}/availability/inventory`} replace />}
          </Route>
          <Route key="/projects/:projectId/reports" path="/projects/:projectId/reports">
            <PageTransition><LazyReports /></PageTransition>
          </Route>
          <Route key="/projects/:projectId/settings" path="/projects/:projectId/settings">
            {(params) => (
              <PageTransition>
                <LazyCurrentProjectSettings selectedProject={params.projectId} />
              </PageTransition>
            )}
          </Route>

          {/* Showroom: the sales-centre touch-screen app, one page per
              section (queue, leads, analytics, plans, setup). The bare path
              opens the queue. */}
          <Route key="/projects/:projectId/showroom" path="/projects/:projectId/showroom">
            {(params) => <Redirect to={`/projects/${params.projectId}/showroom/queue`} replace />}
          </Route>
          <Route key="showroom" path="/projects/:projectId/showroom/:section">
            <PageTransition><LazyShowroom /></PageTransition>
          </Route>

          {/* Agent Portal: a client's broker portal, one page per section.
              Which sections, and so which opens first, depends on whether this
              is the client's Company project or one of its communities, so the
              bare path is the page's to resolve rather than a redirect. */}
          <Route key="/projects/:projectId/agent-portal" path="/projects/:projectId/agent-portal">
            <PageTransition><LazyAgentPortal /></PageTransition>
          </Route>
          <Route key="agent-portal" path="/projects/:projectId/agent-portal/:section">
            <PageTransition><LazyAgentPortal /></PageTransition>
          </Route>

          {/* Email Marketing - Project-scoped route */}
          <Route key="/projects/:projectId/email-marketing" path="/projects/:projectId/email-marketing">
            <PageTransition><LazyEmailMarketing /></PageTransition>
          </Route>

          <Route key="/projects/:projectId/website-analytics" path="/projects/:projectId/website-analytics">
            <PageTransition><LazyWebsiteAnalytics /></PageTransition>
          </Route>

          {/* Account / Security routes */}
          <Route key="/account/security/passkeys" path="/account/security/passkeys">
            <PageTransition><PasskeysSettings /></PageTransition>
          </Route>

          {/* Public content routes - accessible even when authenticated */}
          <Route key="/privacy-policy" path="/privacy-policy">
            <PageTransition><PrivacyPolicy /></PageTransition>
          </Route>
          <Route key="/terms-of-use" path="/terms-of-use">
            <PageTransition><TermsOfUse /></PageTransition>
          </Route>
          <Route key="/quote/:token" path="/quote/:token">
            <PageTransition><PublicQuoteView /></PageTransition>
          </Route>
          <Route key="/worksheet-response/:id" path="/worksheet-response/:id">
            <PageTransition><PublicWorksheetResponse /></PageTransition>
          </Route>
          <Route key="/sign/:publicId" path="/sign/:publicId">
            <PageTransition><SignContract /></PageTransition>
          </Route>
          <Route key="/id/:token" path="/id/:token">
            <PageTransition><IdCapture /></PageTransition>
          </Route>
          <Route key="/confirm-email/:token" path="/confirm-email/:token">
            <PageTransition><ConfirmEmail /></PageTransition>
          </Route>
          <Route key="/unsubscribe/:token" path="/unsubscribe/:token">
            <PageTransition><Unsubscribe /></PageTransition>
          </Route>
          <Route key="/review/:token" path="/review/:token">
            <PageTransition><ReviewEnvelope /></PageTransition>
          </Route>
          <Route key="/documents" path="/documents">
            <PageTransition><PurchaseHome /></PageTransition>
          </Route>
          <Route key="/register/:projectId" path="/register/:projectId">
            <PageTransition><PublicRegistration /></PageTransition>
          </Route>
          <Route key="/roadmap/:token" path="/roadmap/:token">
            <PageTransition><Roadmap /></PageTransition>
          </Route>

          {/* Root → the project picker */}
          <Route key="/" path="/">
            <Redirect to="/projects" replace />
          </Route>

          {/* Unknown routes → the project picker instead of a dead-end 404 */}
          <Route key="*">
            <Redirect to="/projects" replace />
          </Route>
        </Switch>
      </Suspense>
      </ChunkErrorBoundary>
  );
}

// Auth-only routes constant (defined outside component to avoid recreating on every render)
const AUTH_ONLY_ROUTES = ['/login', '/forgot-password', '/reset-password'];

/**
 * Every path a signed-out reader is shown a sign-in screen on: the auth-only
 * routes, and `/` — the installed app's start_url, where a signed-out launch
 * draws Login. Signing in from ANY of these must leave by a real page load
 * (lib/landAfterSignIn.ts); `/` used to fall through to the router's
 * `<Redirect to="/projects">`, and a passkey sign-in, which never sees the 2FA
 * screen, always took that door — a client-side exit from the sign-in frame,
 * and the iOS 26 top-edge blur for the rest of the session.
 * tests/topEdgeSample.test.ts holds the unauthenticated Switch to this list.
 */
const SIGN_IN_SCREEN_ROUTES = [...AUTH_ONLY_ROUTES, '/'];

/**
 * Whether THIS document has drawn a sign-in screen (or the 2FA step). `/` is a
 * page-load exit only then: an ordinary cold launch to `/` while signed in has
 * sampled nothing but the app, and reloading it would cost every launch a
 * second document for nothing — the router's Redirect is right there.
 */
let signInScreenShown = false;

/** The sign-in prediction theme-init.js made before first paint. */
const SIGN_IN_PREDICTED_CLASS = 'auth-backdrop-on';

// Routes canRender2FA refuses to render the 2FA page on — a session that owes
// 2FA must be redirected off these, or it lands in the app with every request
// 403'ing. Must stay in sync with the canRender2FA guard below.
const ROUTES_2FA_CANNOT_RENDER_ON = [...AUTH_ONLY_ROUTES, '/'];

// Public content routes that should always be accessible without authentication or 2FA
const PUBLIC_CONTENT_ROUTES = ['/privacy-policy', '/terms-of-use'];

// Helper function to check if a route is public (accessible without auth or 2FA)
function isPublicContentRoute(path: string): boolean {
  // Check exact matches
  if (PUBLIC_CONTENT_ROUTES.includes(path)) return true;
  // Every public and buyer page, from the one list (lib/publicPages.ts).
  return isPublicPagePath(path);
}

function AppContent() {
  const { user, isAuthenticated, isLoading, twoFactorStatus, logoutInProgress } = useAuth();
  const [location, setLocation] = useLocation();
  const t = useTranslation();
  // One page view per PATH, with ids stripped (lib/analytics.ts), and the tab
  // title set per route.
  usePageViews(location, t);

  // Clear dismissed service notifications on successful OAuth login
  useEffect(() => {
    // Only clear when user first becomes authenticated after OAuth callback
    // This runs once after OAuth redirect but NOT on every session check
    if (isAuthenticated && !isLoading && user && twoFactorStatus) {
      const oauthLoginClearKey = 'oauth-notification-cleared';
      const alreadyCleared = sessionStorage.getItem(oauthLoginClearKey);
      
      // Only clear once per session to avoid repeated clearing
      if (!alreadyCleared) {
        sessionStorage.removeItem('dismissed-notification-id');
        sessionStorage.setItem(oauthLoginClearKey, 'true');
        console.log('[AppContent] OAuth login detected, cleared dismissed service notifications');
      }
    }
  }, [isAuthenticated, isLoading, user, twoFactorStatus]);

  // Auto-sync all projects for SUPER_ADMIN users on login (once per session)
  useEffect(() => {
    console.log('[AppContent] Sync effect triggered:', { isAuthenticated, isLoading, userRole: user?.role, twoFactorRequired: twoFactorStatus?.required });
    // Only sync if authenticated, is SUPER_ADMIN, and 2FA is NOT required
    // CRITICAL: Don't sync if 2FA is required - this indicates incomplete authentication
    if (isAuthenticated && !isLoading && user && user.role === 'SUPER_ADMIN' && twoFactorStatus && !twoFactorStatus.required) {
      const syncKey = 'admin-projects-synced';
      const lastSync = sessionStorage.getItem(syncKey);

      console.log('[AppContent] SUPER_ADMIN detected, checking if sync needed. Last sync:', lastSync);

      // Only sync once per session to avoid redundant requests
      if (!lastSync) {
        console.log('[AppContent] Syncing admin projects...');
        // Use apiRequest to include CSRF token
        import('@/lib/queryClient').then(({ apiRequest }) => {
          apiRequest('POST', '/api/users/sync-admin-projects', undefined, { background: true })
            .then(() => {
              sessionStorage.setItem(syncKey, 'true');
              console.log('✅ Admin projects synchronized');
            })
            .catch(error => {
              // Don't let CSRF errors break the app - just log them
              console.warn('[AppContent] Failed to sync admin projects (non-critical):', error.message || error);
              // Mark as synced anyway to prevent retry loops
              sessionStorage.setItem(syncKey, 'attempted');
            });
        });
      }
    }
  }, [isAuthenticated, isLoading, user, twoFactorStatus]);

  // Redirect logic after authentication
  useEffect(() => {
    // CRITICAL: Block all authentication redirects during logout
    if (logoutInProgress) {
      console.log('[AppContent] Logout in progress, blocking all auth redirects');
      return;
    }
    
    if (isAuthenticated && !isLoading && twoFactorStatus) {
      // CRITICAL: Normalize location by stripping query params before comparing
      // This prevents /login?logout=success from being treated as a protected route
      const normalizedLocation = location.split('?')[0];
      
      // If 2FA is required and we're on a route the 2FA page refuses to render on,
      // redirect to /verify.
      //
      // '/' is in this list because canRender2FA explicitly excludes it. Without
      // the redirect, an authenticated session that still owes 2FA — a bookmark
      // straight to console.salefish.app, or a reload after the 2-hour window
      // lapses — fell through to the full AppShell, where every API call 403s
      // and every list renders empty with no prompt. That is what the 2026-08-11
      // demo looked like from the user's side.
      if (twoFactorStatus.required && ROUTES_2FA_CANNOT_RENDER_ON.includes(normalizedLocation)) {
        console.log('[AppContent] 2FA required, redirecting to /verify');
        setLocation('/verify', { replace: true });
        return;
      }

      // If 2FA is NOT required, land in the app after successful auth — from
      // every screen a sign-in can happen on, `/` included once this document
      // has shown one (SIGN_IN_SCREEN_ROUTES).
      const leavingSignIn = AUTH_ONLY_ROUTES.includes(normalizedLocation)
        || (normalizedLocation === '/' && signInScreenShown);
      if (!twoFactorStatus.required && leavingSignIn) {
        console.log('[AppContent] Auth complete, redirecting to /projects');
        // Always /projects after a fresh login, to let users choose their
        // context — and as a real page load, not a router push: leaving the
        // sign-in frame client-side is what brings the iOS 26 top-edge blur
        // back for the rest of the session (lib/landAfterSignIn.ts).
        landAfterSignIn('/projects');
      }
    }
  }, [isAuthenticated, isLoading, location, setLocation, twoFactorStatus, logoutInProgress]);

  // CRITICAL: Redirect away from /verify if user doesn't need 2FA or isn't authenticated
  useEffect(() => {
    if (location === '/verify') {
      // If not authenticated, redirect to login
      if (!isAuthenticated && !isLoading) {
        console.log('[AppContent] Not authenticated on /verify, redirecting to /login');
        setLocation('/login', { replace: true });
        return;
      }
      
      // If authenticated but 2FA not required, on to /projects — as a page
      // load, like every other door out of the sign-in flow (lib/landAfterSignIn.ts).
      if (isAuthenticated && twoFactorStatus && !twoFactorStatus.required && !isLoading) {
        console.log('[AppContent] 2FA not required on /verify, redirecting to /projects');
        landAfterSignIn('/projects');
        return;
      }
    }
  }, [location, isAuthenticated, isLoading, twoFactorStatus, setLocation]);


  /* theme-init.js predicts a signed-out launch before first paint and puts
     `auth-backdrop-on` on <html>, so the canvas (the iOS status-bar strip) and
     the root top-edge strip start in the sign-in film's ink instead of
     flashing the lavender floor first. AuthBackdrop owns the class while it is
     mounted; when the prediction was wrong — the app, or a public page, is
     what renders — it comes off here, before the paint WebKit samples. Not on
     a sign-in path in the app branch: that render is on its way out by page
     load, and taking the ink off would flash the floor for its last frame. */
  const cleanPath = location.split('?')[0];
  const inApp = !isLoading && isAuthenticated && !!twoFactorStatus && !twoFactorStatus.required
    && !SIGN_IN_SCREEN_ROUTES.includes(cleanPath);
  const onPublicContent = !isLoading && isPublicContentRoute(cleanPath);
  // The card-on-file rule: SaleFish staff and complimentary companies read as
  // never locked. Asked only once the reader is fully in.
  const { data: cardStatus } = useCardStatus(inApp);
  useLayoutEffect(() => {
    if (!inApp && !onPublicContent) return;
    if (document.querySelector('[data-testid="auth-backdrop"]')) return;
    document.documentElement.classList.remove(SIGN_IN_PREDICTED_CLASS);
  }, [inApp, onPublicContent]);

  // Show loading spinner while authentication is being checked
  if (isLoading) {
    // Predicted to land on a sign-in screen: draw nothing over the ink canvas.
    // The shell's skeleton is the lavender floor and would flash between the
    // ink first frame and the film.
    if (document.documentElement.classList.contains(SIGN_IN_PREDICTED_CLASS)) {
      return (
        <div className="min-h-dvh">
          <Toaster />
        </div>
      );
    }
    return (
      <div className="min-h-dvh">
        {/* The page's shape rather than a centred spinner: every screen after
            this one loads as a skeleton, so a cold launch should too. */}
        <PageLoader message={t("shellAppLoading", "Loading")} />
        <Toaster />
      </div>
    );
  }

  // Show login page for unauthenticated users
  if (!isAuthenticated) {
    // CRITICAL: Strip query params for route matching (e.g. /login?session_expired=1)
    const cleanLocation = location.split('?')[0];

    // CRITICAL: Redirect to /login if trying to access protected routes
    // Allow public routes: /login, /forgot-password, /reset-password, /register/:projectId, /privacy-policy, /terms-of-use, /quote/:token
    const isPublicRoute = cleanLocation === '/login' ||
                         cleanLocation === '/forgot-password' ||
                         cleanLocation === '/reset-password' ||
                         cleanLocation === '/' ||
                         isPublicContentRoute(cleanLocation);
    
    if (!isPublicRoute) {
      // A <Redirect>, not setLocation() here: navigating DURING this render
      // updated every location subscriber mid-render — React's "Cannot update
      // a component (AppContent) while rendering a different component", on
      // every sign-out that landed on a protected route (QA 2026-10-28).
      // Redirect navigates from a layout effect, after the render commits.
      return (
        <div className="flex items-center justify-center h-screen">
          <Redirect to="/login" replace />
          <Loader size="md" />
        </div>
      );
    }
    
    // The sign-in screens draw the service banner themselves, under their own
    // top bar (AuthPageShell); the buyer-facing pages get it here.
    const onSignInScreen = SIGN_IN_SCREEN_ROUTES.includes(cleanLocation);
    if (onSignInScreen) signInScreenShown = true;
    return (
      <>
        {!onSignInScreen && <ServiceNotificationBanner />}
        <PublicPages>
          <Switch>
            <Route path="/register/:projectId" component={PublicRegistration} />
            <Route path="/login" component={Login} />
            <Route path="/forgot-password" component={ForgotPassword} />
            <Route path="/reset-password" component={ResetPassword} />
            <Route path="/privacy-policy" component={PrivacyPolicy} />
            <Route path="/terms-of-use" component={TermsOfUse} />
            <Route path="/quote/:token" component={PublicQuoteView} />
            <Route path="/worksheet-response/:id" component={PublicWorksheetResponse} />
            <Route path="/sign/:publicId" component={SignContract} />
            <Route path="/id/:token" component={IdCapture} />
            <Route path="/confirm-email/:token" component={ConfirmEmail} />
            <Route path="/unsubscribe/:token" component={Unsubscribe} />
            <Route path="/review/:token" component={ReviewEnvelope} />
            <Route path="/documents" component={PurchaseHome} />
            <Route path="/roadmap/:token" component={Roadmap} />
            <Route path="/" component={Login} />
          </Switch>
        </PublicPages>
        <Toaster />
      </>
    );
  }

  // CRITICAL SAFEGUARD: If user exists but twoFactorStatus is null, we're in a transitional state
  // This can happen during logout. Show loading instead of trying to render 2FA page
  if (isAuthenticated && user && twoFactorStatus === null) {
    console.log('[AppContent] Transitional state (user exists but no 2FA status), showing loader');
    return (
      <div className="min-h-dvh">
        {/* The page's shape rather than a centred spinner: every screen after
            this one loads as a skeleton, so a cold launch should too. */}
        <PageLoader message={t("shellAppLoading", "Loading")} />
        <Toaster />
      </div>
    );
  }

  // Show 2FA verification for authenticated users who need it
  // CRITICAL: Only render if we have confirmed authentication AND 2FA is required
  // AND we're not on auth-only routes (prevents showing 2FA after logout redirect)
  // CRITICAL: Block 2FA rendering during logout to prevent race conditions
  // CRITICAL: Also skip 2FA for public content routes (privacy policy, terms, public quotes, registration)
  const normalizedLocationFor2FA = location.split('?')[0]; // Strip query params for comparison
  
  // CRITICAL: If user is on a public content route, render it directly (without 2FA or AppShell)
  // This allows access to privacy policy, terms, public quotes even when 2FA is pending
  if (isPublicContentRoute(normalizedLocationFor2FA)) {
    console.log('[AppContent] Rendering public content route:', normalizedLocationFor2FA);
    return (
      <>
        <ServiceNotificationBanner />
        <PublicPages>
          <Switch>
            <Route path="/privacy-policy" component={PrivacyPolicy} />
            <Route path="/terms-of-use" component={TermsOfUse} />
            <Route path="/quote/:token" component={PublicQuoteView} />
            <Route path="/worksheet-response/:id" component={PublicWorksheetResponse} />
            <Route path="/sign/:publicId" component={SignContract} />
            <Route path="/id/:token" component={IdCapture} />
            <Route path="/confirm-email/:token" component={ConfirmEmail} />
            <Route path="/unsubscribe/:token" component={Unsubscribe} />
            <Route path="/review/:token" component={ReviewEnvelope} />
            <Route path="/documents" component={PurchaseHome} />
            <Route path="/register/:projectId" component={PublicRegistration} />
            <Route path="/roadmap/:token" component={Roadmap} />
          </Switch>
        </PublicPages>
        <Toaster />
      </>
    );
  }
  
  // CRITICAL: Extra safety — never render 2FA on explicitly public/auth-only routes
  // or when user session is null (defensive against stale state after logout)
  const canRender2FA = !logoutInProgress
    && isAuthenticated
    && !!user
    && twoFactorStatus?.required === true
    && !AUTH_ONLY_ROUTES.includes(normalizedLocationFor2FA)
    && normalizedLocationFor2FA !== '/';

  if (canRender2FA) {
    console.log('[AppContent] Rendering 2FA verification page');
    signInScreenShown = true;
    return (
      <>
        {/* The service banner is drawn by AuthPageShell, under its top bar. */}
        <PublicPages>
        <TwoFactorVerificationPage onVerified={() => {
          // Navigate EXPLICITLY rather than leaving it to the /verify effect.
          //
          // Relying on that effect made the landing conditional on a render where
          // location is still exactly '/verify' AND isLoading is false AND
          // twoFactorStatus is non-null. Miss any one and verification succeeds
          // while the screen never moves — no navigation, no requests, nothing to
          // do but reload. Observed 2026-08-12: 2fa/verify 200 at 11:59:50 and not
          // one request until a manual refresh at 11:59:59.
          //
          // And as a real page load (lib/landAfterSignIn.ts), which also does
          // what the optimistic `setTwoFactorVerified()` + `checkSession()` pair
          // here used to: the new document re-reads the session the backend has
          // already saved (twoFactorVerified = true), and the 2FA card stays put
          // until it lands instead of flashing the shell at '/verify' for a frame.
          // Back to where the reader was going before sign-in, if anywhere (G4).
          landAfterSignIn(consumeReturnTo() ?? '/projects');
        }} />
        </PublicPages>
        <Toaster />
      </>
    );
  }

  // Public quote pages — render without AppShell (no sidebar/navigation)
  // Authenticated users previewing a quote should see the same clean page as customers
  if (location.startsWith('/quote/')) {
    return (
      <>
        <ServiceNotificationBanner />
        <PublicPages>
          <Switch>
            <Route path="/quote/:token" component={PublicQuoteView} />
          </Switch>
        </PublicPages>
        <Toaster />
      </>
    );
  }

  // A company SaleFish bills, with no card on file past its deadline: the
  // server refuses everything else (402), so draw the one screen that fixes it.
  if (cardStatus?.locked) {
    return (
      <Suspense fallback={<PageLoader message={t("shellAppLoading", "Loading")} />}>
        <CardGate status={cardStatus} />
      </Suspense>
    );
  }

  // Show main app with AppShell (includes dual-context navigation)
  return (
    <CompanyThemeProvider>
      <UserPreferencesSync />
      {/* Only here, in the fully-authenticated branch: the idle clock has
          nothing to guard on /login, and the 2FA screen has its own expiry. */}
      <IdleTimeout />
      {/* The service banner is inside AppShell's sticky header, under its
          content row — so the top of the screen is always the header's own
          ground, which is what the root top-edge strip is coloured for. */}
      {/* The shell answers to `useAppLocation`: while Platform or Company
          Settings is up as a layer, the page underneath keeps the location it
          had. */}
      <WouterRouter hook={useAppLocation} searchHook={useAppSearch}>
        <AppShell>
          <Router />
        </AppShell>
      </WouterRouter>
      <PlatformLayer />
      <CompanyLayer />
      <ChunkErrorBoundary fallback={null}>
        <Suspense fallback={null}>
          <PromptLayer />
        </Suspense>
      </ChunkErrorBoundary>
      {/* The 2FA window lapsing mid-task opens this in place and re-sends the
          original request once confirmed — nothing retyped, no navigation (G7). */}
      <StepUpDialog />
      <Toaster />
    </CompanyThemeProvider>
  );
}

// framer-motion's animation engine, fetched only once the app is running.
//
// Every animated surface in this app uses `m.*` rather than `motion.*`, which
// means the engine is not statically reachable and lands in its own async
// chunk instead of the entry. Until this was done, AppShell/PageTransition/
// RouteProgress dragged all 122 kB of it onto /login, a route that renders no
// shell and no page transition.
//
// While the import is in flight `m` elements render at their initial styles and
// simply do not animate — no layout shift, no missing content, and in practice
// it resolves long before the first transition is triggered.
//
// `strict` makes a stray `motion.*` throw in development instead of silently
// pulling the whole engine back into the entry chunk (J7). `MotionConfig
// reducedMotion="user"` is the reader's OS setting: the CSS backstop in
// index.css zeroes CSS transitions but cannot reach framer's JS-driven ones,
// which kept sliding and springing for anyone who had asked for less (A19).
const loadMotionFeatures = () => import("@/lib/motionFeatures").then(mod => mod.default);

/* Installs the app-wide document watchers.

   A component rather than a call in main.tsx so the layout harness gets it
   too — the harness has its own entry point and imports App directly, so
   anything wired into main.tsx is invisible to it.

   There is no scroll-lock module any more: an open Dialog, Sheet, Select or
   menu locks the page in CSS alone (index.css, `html:has(> body[data-scroll-
   locked])`). The old one pinned <body> with `position: fixed` on touch
   devices, which threw the sticky header off-screen on every open. */
function DocumentWatchers() {
  // The keyboard's edge, as CSS custom properties — index.css §THE VISUAL VIEWPORT.
  useEffect(() => installVisualViewportVars(), []);
  // "More table this way" on every sideways scroller — lib/scrollMore.ts.
  useEffect(() => installScrollMore(), []);
  return null;
}

function App() {
  return (
    /* The last line of defence: anything the per-page boundaries do not catch
       (a provider, the shell) lands on "This page needs a refresh" rather than
       a white screen (A4/E6). */
    <AppErrorBoundary>
    <LazyMotion features={loadMotionFeatures} strict>
    <MotionConfig reducedMotion="user">
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <ThemeProvider defaultTheme="system" storageKey="salefish-theme">
            <LanguageProvider storageKey="salefish-language">
              <RouteProgressProvider>
              <IntelligenceProvider>
                <WidgetManagerProvider>
                  <UIProvider>
                    <DocumentWatchers />
                    {/* Mounted once, above every route, and never unmounted: the
                        element iOS reads the top-edge colour from. It has to
                        outlive every header and the sign-in frame — see
                        ui/top-edge-sample.tsx. */}
                    <TopEdgeSample persistent />
                    <AppContent />
                    {/* Rendered once at the root, outside AppContent's routing,
                        so a pending update stays visible across navigation and
                        on the unauthenticated pages too — a stale login screen
                        is just as broken as a stale dashboard. Inside
                        LanguageProvider because it renders translated copy. */}
                    <ChunkErrorBoundary fallback={null}>
                      <Suspense fallback={null}>
                        <UpdatePrompt />
                      </Suspense>
                    </ChunkErrorBoundary>
                  </UIProvider>
                </WidgetManagerProvider>
              </IntelligenceProvider>
              </RouteProgressProvider>
            </LanguageProvider>
          </ThemeProvider>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
    </MotionConfig>
    </LazyMotion>
    </AppErrorBoundary>
  );
}

export default App;